import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = "https://apis.pluglr.com/pluglr";

// Async thunk for searching users
export const searchUsers = createAsyncThunk(
  "cohosts/searchUsers",
  async ({ key, page = 1, pageSize = 10 }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/users/search/${key}/${page}/${pageSize}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to search users"
      );
    }
  }
);

// Async thunk for adding cohosts
export const addCohost = createAsyncThunk(
  "cohosts/addCohost",
  async ({ eventId, userIds }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.put(
        `${BASE_URL}/api/v1/auth/events/cohosts/add/${eventId}`,
        userIds, // Array of user IDs
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add cohost"
      );
    }
  }
);

// Async thunk for removing cohost
export const removeCohost = createAsyncThunk(
  "cohosts/removeCohost",
  async ({ eventId, userIds }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      // The API documentation says body contains user ids like ["id1", "id2"]
      // But usually DELETE requests don't have a body in some standard implementations,
      // however axios supports data in config for delete, or we can use put/post if the API is designed that way.
      // The user said: "the api to remove cohost is {{BASE_URL}}/api/v1/auth/events/cohosts/remove/{{evtId}} the bogy too contains user ids"
      // So it's likely a PUT or POST or DELETE with body.
      // Assuming it follows the add pattern which is PUT, or maybe DELETE with data.
      // Let's check the user request again: "the api to remove cohost is {{BASE_URL}}/api/v1/auth/events/cohosts/remove/{{evtId}} the bogy too contains user ids"
      // It doesn't explicitly say the method, but usually if it has a body it might be PUT or POST.
      // However, standard REST for remove is DELETE. Axios delete accepts `data` in the config object.
      // Let's try DELETE with data first, or maybe PUT if it's symmetric to add.
      // Wait, the user said "PUT .../add/eventId".
      // I will assume it is PUT for remove as well if it's a "toggle" or similar, OR it could be DELETE.
      // Let's look at the `addCohost` again. It is PUT.
      // I'll use PUT for remove as well to be safe if it's a custom API, or maybe POST.
      // Actually, let's try to use `axios.request` or just assume it's PUT for now as it modifies the resource.
      // If it fails I can change it. But wait, "remove" usually implies DELETE.
      // Let's use `axios.delete` with `data` payload.

      const response = await axios.put(
        `${BASE_URL}/api/v1/auth/events/cohosts/remove/${eventId}`,
        userIds,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove cohost"
      );
    }
  }
);

const cohostSlice = createSlice({
  name: "cohosts",
  initialState: {
    searchResults: [],
    loading: false,
    error: null,
    addCohostSuccess: false,
    removeCohostSuccess: false,
  },
  reducers: {
    resetCohostState: (state) => {
      state.addCohostSuccess = false;
      state.removeCohostSuccess = false;
      state.error = null;
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Search Users
      .addCase(searchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload?.data || []; // Assuming data is in payload.data
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Cohost
      .addCase(addCohost.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.addCohostSuccess = false;
      })
      .addCase(addCohost.fulfilled, (state) => {
        state.loading = false;
        state.addCohostSuccess = true;
      })
      .addCase(addCohost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove Cohost
      .addCase(removeCohost.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.removeCohostSuccess = false;
      })
      .addCase(removeCohost.fulfilled, (state) => {
        state.loading = false;
        state.removeCohostSuccess = true;
      })
      .addCase(removeCohost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetCohostState } = cohostSlice.actions;
export default cohostSlice.reducer;
